import { useStoreQuery, queries } from "@/lib/store";
import { PillTag } from "./UI";

/**
 * SabbathHeroCard — full devotional card shown on Today when the sister's
 * Sabbath is active. Replaces the macros/streak striving frame for one day.
 * Quiet, gold-warm, no metrics. The day's reflection is the whole UI.
 */
interface Props {
  userId: string;
  variant?: "today" | "preview";
}

export default function SabbathHeroCard({ userId, variant = "today" }: Props) {
  const devotional = useStoreQuery(() => queries.sabbathDevotionalForToday(userId));
  if (!devotional) return null;

  const eyebrow = variant === "today" ? "Sabbath today" : "Your Sabbath devotional";

  return (
    <div
      data-testid="card-sabbath-hero"
      style={{
        position: "relative",
        borderRadius: 22,
        overflow: "hidden",
        padding: "22px 22px 24px",
        background:
          "linear-gradient(155deg, #1E4A35 0%, #2C6248 55%, #3a7a5a 100%)",
        color: "#F5F0E8",
        boxShadow:
          "0 18px 38px -22px rgba(22,56,40,0.55), 0 1px 0 rgba(245,240,232,0.06) inset",
      }}
    >
      {/* Gold halo behind passage */}
      <span
        aria-hidden
        style={{
          position: "absolute",
          top: -60,
          right: -40,
          width: 220,
          height: 220,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(160,120,32,0.32) 0%, rgba(160,120,32,0) 65%)",
          pointerEvents: "none",
        }}
      />

      <div className="flex items-center justify-between mb-3 relative">
        <PillTag tone="gold">{eyebrow}</PillTag>
        <span
          className="mnf-eyebrow"
          style={{ color: "rgba(245,240,232,0.62)", fontSize: 10 }}
        >
          {devotional.reference}
        </span>
      </div>

      <h2
        className="mnf-serif"
        style={{
          fontSize: 26,
          fontWeight: 500,
          lineHeight: 1.18,
          marginBottom: 14,
          position: "relative",
        }}
      >
        {devotional.title}
      </h2>

      <blockquote
        className="mnf-serif italic"
        style={{
          fontSize: 17,
          lineHeight: 1.45,
          color: "rgba(245,240,232,0.94)",
          borderLeft: "2px solid rgba(160,120,32,0.6)",
          paddingLeft: 14,
          margin: 0,
          marginBottom: 16,
          position: "relative",
        }}
      >
        "{devotional.passage}"
      </blockquote>

      <p
        className="mnf-body"
        style={{
          fontSize: 13.5,
          lineHeight: 1.6,
          color: "rgba(245,240,232,0.88)",
          marginBottom: 14,
          position: "relative",
        }}
      >
        {devotional.reflection}
      </p>

      <div
        style={{
          paddingTop: 14,
          borderTop: "1px solid rgba(245,240,232,0.14)",
          position: "relative",
        }}
      >
        <p
          className="mnf-eyebrow"
          style={{ color: "rgba(160,140,80,0.95)", marginBottom: 6, fontSize: 10 }}
        >
          A small invitation
        </p>
        <p
          className="mnf-body"
          style={{
            fontSize: 13,
            lineHeight: 1.55,
            color: "rgba(245,240,232,0.92)",
          }}
        >
          {devotional.invitation}
        </p>
      </div>
    </div>
  );
}
